import { BrowserRouter as Router, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { TourProvider } from './context/TourContext';
import { DietGenerationProvider } from './context/DietGenerationContext.js';
import LandingPage from './pages/NewLandingPage/LandingPage.js';
import ContactUs from './pages/Contacts/Contacts.js';
import Pricing from './pages/Pricing/Pricing.js';
import RegisterPage from './pages/NewRegisterPage/RegisterPage.js';
import Footer from './components/footer/Footer.js';
import Sidebar from './components/Sidebar/Sidebar.js';
import Navbar from './components/navbar/Navbar.js';
import TopNavbar from './components/TopNavbar/TopNavbar.js';
import BottomNavbar from './components/BottomNavbar/BottomNavbar.js';
import NotFound from './pages/NotFound/NotFound.js';
import Home from './pages/Home/Home.js'
import HomePage from './pages/NewHomePage/HomePage.js'
import Clients from './pages/Clients/Clients.js'
import Calendar from './pages/NewCalendar/Calendar.js';
import Settings from './pages/Settings/Settings.js';
import './App.css';
import ClientDetails from './pages/ClientDetails/ClientDetails.js';
import LoginPage from './pages/NewLoginPage/LoginPage.js';
import GetStartedPage from './pages/GetStartedPage/GetStartedPage.js';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage.js';
import FoodsAndRecipes from './pages/FoodsAndRecipes/FoodsAndRecipes.js';
import Profile from './pages/Profile/Profile.js';
import CheckEmailPage from './pages/CheckEmailPage/CheckEmailPage.js';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute.js';
import DietTemplates from './pages/DietTemplates/DietTemplates.js';

/**
 * Wrapper that keys DietGenerationProvider on clientID so the context is
 * fully reset whenever the user navigates to a different client's page.
 */
const ClientDetailsPage = () => {
    const { clientID } = useParams();
    return (
        <DietGenerationProvider key={clientID}>
            <ClientDetails />
        </DietGenerationProvider>
    );
};

const AppContent = () => {
    const location = useLocation();

    // Routes that show the authenticated layout (sidebar + top navbar)
    const sidebarRoutes   = ['/home', '/clients', '/calendar', '/settings', '/client/', '/foods', '/profile', '/templates'];
    const topNavbarRoutes = ['/home', '/clients', '/calendar', '/settings', '/client/', '/foods', '/profile', '/templates'];

    const showSidebar   = sidebarRoutes.some(route => location.pathname.startsWith(route));
    const showTopNavBar = topNavbarRoutes.some(route => location.pathname.startsWith(route));

    return (
        <TourProvider>
        <AppProvider>
            {showSidebar ? (
                /*
                 * Authenticated layout
                 * ┌─────────┬──────────────────────────────────────┐
                 * │ Sidebar │ TopNavbar                            │
                 * │ (60 px) ├──────────────────────────────────────┤
                 * │  flex   │ .app-page-content  (scrollable)      │
                 * │  item   │                                      │
                 * └─────────┴──────────────────────────────────────┘
                 * Sidebar keeps a 60 px layout slot; pages need zero
                 * margin/padding — they fill whatever space remains.
                 */
                <div className="app-authenticated-layout">
                    <Sidebar />
                    <div className="app-main-col">
                        {showTopNavBar && <TopNavbar />}
                        <div className="app-page-content">
                            <ProtectedRoute>
                                <Routes>
                                    <Route exact path="/home"         element={<HomePage />} />
                                    <Route exact path="/calendar"     element={<Calendar />} />
                                    <Route exact path="/clients"      element={<Clients />} />
                                    <Route path="/client/:clientID"   element={<ClientDetailsPage />} />
                                    <Route exact path="/foods"        element={<FoodsAndRecipes />} />
                                    <Route exact path="/templates"    element={<DietTemplates />} />
                                    <Route exact path="/settings"     element={<Settings />} />
                                    <Route exact path="/profile"      element={<Profile />} />
                                </Routes>
                            </ProtectedRoute>
                        </div>
                    </div>
                    <BottomNavbar />
                </div>
            ) : (
                /* Public layout — full-width pages + footer */
                <>
                    <Routes>
                        <Route exact path="/"            element={<LandingPage />} />
                        <Route exact path="/contacts"    element={<ContactUs />} />
                        <Route exact path="/pricing"     element={<Pricing />} />
                        <Route exact path="/register"    element={<RegisterPage />} />
                        <Route exact path="/check-email" element={<CheckEmailPage />} />
                        <Route exact path="/login"       element={<LoginPage />} />
                        <Route exact path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route exact path="/reset-password"  element={<ResetPasswordPage />} />
                        <Route exact path="/get-started" element={<GetStartedPage />} />
                        <Route path="*"                  element={<NotFound />} />
                    </Routes>
                </>
            )}
        </AppProvider>
        </TourProvider>
    );
};


const App = () => (
    <Router>
        <div id='App'>
            <AppContent />
        </div>
    </Router>
);

export default App;
